import { SMS_SENDER_LABEL_FALLBACK, SMS_SENDER_LABEL_MAX } from './constants/index.ts';

/**
 * How many segments a message body will be billed as.
 *
 * A carrier bills per segment, not per message, and the boundary is not where
 * people expect: 160 characters for GSM-7, but a single character outside that
 * alphabet — a curly apostrophe pasted from a word processor, an accented name,
 * an emoji — forces the whole message into UCS-2 and drops the segment to 70.
 * A concatenated message loses further room to its header, so the per-segment
 * capacity falls again once there is more than one.
 *
 * This exists because Twilio's own count is not always available when a message
 * is logged: a Messaging Service accepts a message and reports
 * `num_segments: "0"` while segmentation is still pending. Rather than record a
 * zero (or a fabricated 1) against a message that is really three, the log
 * takes this estimate and the delivery callback replaces it with the provider's
 * final figure when that arrives.
 */

const GSM7_SINGLE = 160;
const GSM7_CONCATENATED = 153;
const UCS2_SINGLE = 70;
const UCS2_CONCATENATED = 67;

/**
 * The GSM 03.38 basic alphabet, plus the extension characters that are encoded
 * as two septets each (`^{}\[~]|€`).
 */
const GSM7_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

const GSM7_EXTENDED = '^{}\\[~]|€';

/** Whether every character fits GSM-7, which decides the segment size. */
export function isGsm7(text: string): boolean {
  for (const character of text) {
    if (!GSM7_BASIC.includes(character) && !GSM7_EXTENDED.includes(character)) return false;
  }
  return true;
}

/**
 * The characters worth spelling out rather than dropping.
 *
 * Business names arrive by copy and paste, so the ones that actually turn up
 * are typographic: the quotes and dashes a word processor substitutes while you
 * type, and the space that looks identical to a normal one.
 */
const GSM7_SUBSTITUTIONS: Record<string, string> = {
  '‘': "'",
  '’': "'",
  '‚': "'",
  '‛': "'",
  '“': '"',
  '”': '"',
  '„': '"',
  '–': '-',
  '—': '-',
  '−': '-',
  '…': '...',
  '•': '*',
  '·': '.',
  ' ': ' ',
  ' ': ' ',
  ' ': ' ',
  '™': 'TM',
  '©': '(c)',
  '®': '(r)',
};

/**
 * Rewrites text into the GSM-7 alphabet, losing what cannot be represented.
 *
 * For anything the customer must be able to read this would be vandalism — a
 * message is worth a second segment. It exists for the one string that is
 * repeated on every message and chosen by someone else: the sender label. A
 * curly apostrophe in a company name would otherwise push every reminder for
 * that tenant into UCS-2, halving the segment size and doubling the bill on the
 * shared account for a character nobody meant to type.
 *
 * Accents are folded to their base letter rather than dropped, so "Björn" reads
 * as "Bjorn" instead of "Bjrn". The result can be empty — a name written
 * entirely in a non-Latin script has no GSM-7 form at all — and callers are
 * expected to have a fallback.
 */
export function toGsm7(text: string): string {
  let out = '';

  for (const character of text) {
    if (GSM7_BASIC.includes(character) || GSM7_EXTENDED.includes(character)) {
      out += character;
      continue;
    }

    const substitute = GSM7_SUBSTITUTIONS[character];
    if (substitute !== undefined) {
      out += substitute;
      continue;
    }

    // Decomposing separates a letter from its accent, so the letter survives
    // when the combined form is not representable.
    for (const folded of character.normalize('NFD').replace(/\p{M}/gu, '')) {
      if (GSM7_BASIC.includes(folded) || GSM7_EXTENDED.includes(folded)) out += folded;
    }
  }

  return out;
}

/**
 * Spaces `Intl` inserts inside a formatted amount, which are not GSM-7 and are
 * not worth a segment: the locale means an ordinary space by all of them.
 */
const MONEY_SPACES = /[\u00a0\u202f\u2007\u2009]/g;

/**
 * An already-formatted amount, made safe to put in a text.
 *
 * Money is the one interpolated value whose *locale* can move a whole message
 * out of GSM-7: `es-ES` writes "1234,56 US$" with a non-breaking space before
 * the symbol, and that single U+00A0 cuts the segment from 160 characters to
 * 70. Replacing it with an ordinary space is invisible to the reader and saves
 * the segment.
 *
 * Only the spacing. An earlier version ran the whole value through `toGsm7`,
 * which *drops* what it cannot represent — and ₹, ₩, ₪, ₱, ₫ and ฿ are all
 * outside GSM-7 and all in `SUPPORTED_CURRENCY_CODES`, so "₹1,234.50" became
 * "1,234.50": a payment request naming no currency at all. A message that costs
 * two segments is a cost; a message that does not say what money it is asking
 * for is a defect. So a symbol outside the alphabet is left alone and the
 * segment is paid.
 */
export function smsSafeAmount(formatted: string): string {
  return formatted.replace(MONEY_SPACES, ' ');
}

/** How far back a cut will hunt for a space rather than land inside a word. */
const WORD_BREAK_LOOKBACK = 16;

/**
 * Trims the one variable part of a message until the whole message fits.
 *
 * Every message this app sends is assembled rather than typed, and most of the
 * assembled parts have a length nobody chooses — a label that is capped, a
 * quote number, a formatted total, a link. The exception is free text somebody
 * typed into a box, and that is the part that decides whether a message costs
 * one segment or six. Refusing the send over it would be the wrong trade (the
 * note is the least important thing in the message), so it is cut instead —
 * and cutting it is why this takes an `assemble` callback rather than a string:
 * the budget belongs to the finished message, but only the part handed in may
 * be spent from it. A helper that trimmed a string in isolation would leave
 * the caller to work out how much room the link left, which is the arithmetic
 * that gets a link cut in half.
 *
 * The cut is measured in segments rather than characters because the two do not
 * correspond: the same 200 characters are two segments in GSM-7 and three in
 * UCS-2, and a single emoji anywhere in the note moves the *whole message*
 * between those worlds. Measuring the assembled body is what accounts for that;
 * hence the binary search over `estimateSmsSegments` rather than a slice at a
 * fixed offset.
 *
 * Cutting mid-word reads as a fault rather than as a trim, so the cut moves
 * back to the last space when one is close enough to be the word it landed in.
 * The ellipsis is ASCII: the single-character '…' is not in GSM-7 and would
 * push a message that just fit back over the line it was trimmed to clear.
 *
 * Returns the empty string when even an ellipsis will not fit — the caller is
 * expected to leave the part out entirely rather than pay a segment for it.
 */
export function fitWithinSegments(
  part: string,
  maxSegments: number,
  assemble: (part: string) => string = (value) => value,
  ellipsis = '...',
): string {
  if (maxSegments < 1) return '';
  if (estimateSmsSegments(assemble(part)) <= maxSegments) return part;

  // Code points, not UTF-16 units: slicing an emoji in half produces a lone
  // surrogate, which is neither displayable nor a saving.
  const characters = [...part];

  const candidateAt = (length: number) => characters.slice(0, length).join('').trimEnd() + ellipsis;

  // The answer is monotonic in length — a longer part never assembles into
  // fewer segments — so the longest one that fits is found in a handful of
  // estimates rather than one per character.
  let low = 0;
  let high = characters.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (estimateSmsSegments(assemble(candidateAt(middle))) <= maxSegments) low = middle;
    else high = middle - 1;
  }

  const cut = characters.slice(0, low).join('').trimEnd();
  if (!cut) return '';

  const lastSpace = cut.lastIndexOf(' ');
  const trimmed = lastSpace > 0 && cut.length - lastSpace <= WORD_BREAK_LOOKBACK ? cut.slice(0, lastSpace) : cut;
  return trimmed.trimEnd() + ellipsis;
}

/** Conservative billing estimate: escape sequences/surrogates stay intact.
 * Unknown sender pools include toll-free's smaller multipart capacity. */
export function estimateSmsSegments(body: string, sender: 'standard' | 'toll_free' | 'unknown' = 'unknown'): number {
  if (!body) return 1;
  const gsm = isGsm7(body);
  // Twilio packs intact graphemes, including modifier/ZWJ emoji. Reserving
  // individual code points can undercount multipart messages.
  const characters = gsm
    ? Array.from(body)
    : Array.from(new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(body), (item) => item.segment);
  const sizes = characters.map((character) => (gsm ? (GSM7_EXTENDED.includes(character) ? 2 : 1) : character.length));
  const total = sizes.reduce((sum, size) => sum + size, 0);
  if (total <= (gsm ? GSM7_SINGLE : UCS2_SINGLE)) return 1;
  const capacity = sender === 'standard' ? (gsm ? GSM7_CONCATENATED : UCS2_CONCATENATED) : gsm ? 152 : 66;
  let segments = 1,
    used = 0;
  for (const size of sizes) {
    if (size > capacity) {
      if (used > 0) {
        segments++;
        used = 0;
      }
      segments += Math.ceil(size / capacity) - 1;
      used = capacity;
      continue;
    }
    if (used + size > capacity) {
      segments++;
      used = 0;
    }
    used += size;
  }
  return segments;
}

/** A label reduced to the alphabet the whole message is priced on. */
function gsm7Label(value: string | null): string {
  return toGsm7(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * What the customer sees the message is from.
 *
 * Shared rather than worker-only because the composer has to price a text
 * BEFORE sending it, and it can only do that honestly by assembling the same
 * string the server will: one character outside GSM-7 in this label — a curly
 * apostrophe in "Maria's", an emoji somebody put in the company name — halves
 * the segment size of the whole message. A browser estimate that measured the
 * raw name would refuse to send a message the server would have accepted.
 *
 * Long names are billed for, so the label is folded to GSM-7 and capped rather
 * than passed through.
 */
export function smsSenderLabel(companyName: string, configured: string | null): string {
  const label = gsm7Label(configured) || gsm7Label(companyName) || SMS_SENDER_LABEL_FALLBACK;
  return label.length > SMS_SENDER_LABEL_MAX ? `${label.slice(0, SMS_SENDER_LABEL_MAX - 1).trimEnd()}.` : label;
}

/**
 * Prefix used when a text leaves on the deployment's shared number.
 *
 * The shared number belongs to BlastoClean, and the message names a cleaning
 * company. Carriers expect both to be visible, so the label the customer
 * already sees gains "via {app}". The company's own number does not use this:
 * that number is the company's, and the label is the registered business name.
 */
export function attributeSharedSender(body: string, appName: string): string {
  const brand = gsm7Label(appName) || 'BlastoClean';
  const suffix = ` via ${brand}`;
  const colon = body.indexOf(':');
  if (colon <= 0) return `${brand}: ${body}`;
  const label = body.slice(0, colon);
  if (label.endsWith(suffix)) return body;
  return `${label}${suffix}${body.slice(colon)}`;
}

/**
 * Replaces whatever label a text was composed with, with the registered name.
 *
 * Owners can type a different "name texts are sent under", including one per
 * location. On a company's own number the name in the text has to be the name
 * the carriers registered, so a custom label is not what goes out.
 */
export function lockToRegisteredSender(body: string, legalName: string): string {
  const label = smsSenderLabel(legalName, null);
  const colon = body.indexOf(':');
  if (colon <= 0) return `${label}: ${body}`;
  return `${label}${body.slice(colon)}`;
}

/**
 * Rewrites a body, then keeps the result inside the segment cap.
 *
 * Builders fit the message before they know which sender will carry it. The
 * shared-number suffix ("via BlastoClean") and a longer registered name are
 * applied later, and either one can push a message that was exactly on the
 * cap over it. This measures the rewritten text.
 *
 * When `trim` is set, the cut is taken from the text before a trailing link
 * so the link itself stays intact. A person's own message is never trimmed:
 * pass `trim: false` and a null result means the send must be refused.
 */
export function rewriteWithinSegments(
  body: string,
  maxSegments: number,
  rewrite: (value: string) => string,
  trim: boolean,
): string | null {
  const rewritten = rewrite(body);
  if (estimateSmsSegments(rewritten) <= maxSegments) return rewritten;
  if (!trim) return null;

  const urlAt = body.search(/\shttps?:\/\/\S+\s*$/);
  if (urlAt > 0) {
    const head = body.slice(0, urlAt);
    const tail = body.slice(urlAt);
    const fittedHead = fitWithinSegments(head, maxSegments, (part) => rewrite(`${part}${tail}`));
    if (fittedHead) return rewrite(`${fittedHead}${tail}`);
  }

  const fitted = fitWithinSegments(body, maxSegments, rewrite);
  if (!fitted) return null;
  return rewrite(fitted);
}
